<svelte:options customElement={{ tag: "pension-planner", shadow: "none" }} />

<script lang="ts">
    import { PensionCalculator } from "../../lib/pension-planner";
    import type { PensionPlannerInputs, PensionPlannerResult } from "../../lib/pension-planner";

    // Default values
    const DEFAULT_CURRENT_VALUE = 50000;
    const DEFAULT_CONTRIBUTION_AMOUNT = 500;
    const DEFAULT_CONTRIBUTION_FREQUENCY: "monthly" | "annual" = "monthly";
    const DEFAULT_EXPECTED_ANNUAL_RETURN = 7;
    const DEFAULT_EXPECTED_ANNUAL_INFLATION = 2.5;
    const DEFAULT_CURRENT_AGE = 35;
    const DEFAULT_YEARS_TO_PROJECT = 30;
    const DEFAULT_TAX_FREE_LUMP_SUM_PERCENTAGE = 25;

    let currentValue: number = $state(DEFAULT_CURRENT_VALUE);
    let contributionAmount: number = $state(DEFAULT_CONTRIBUTION_AMOUNT);
    let contributionFrequency: "monthly" | "annual" = $state(DEFAULT_CONTRIBUTION_FREQUENCY);
    let expectedAnnualReturn: number = $state(DEFAULT_EXPECTED_ANNUAL_RETURN);
    let expectedAnnualInflation: number = $state(DEFAULT_EXPECTED_ANNUAL_INFLATION);
    let currentAge: number = $state(DEFAULT_CURRENT_AGE);
    let yearsToProject: number = $state(DEFAULT_YEARS_TO_PROJECT);
    let taxFreeLumpSumPercentage: number = $state(DEFAULT_TAX_FREE_LUMP_SUM_PERCENTAGE);

    let result: PensionPlannerResult | null = $state(null);
    let calculationError: string | null = $state(null);
    let isAuthenticated: boolean = $state(false);
    let lastSaveTime: number = $state(0);
    let isLoading: boolean = $state(false);

    async function loadParameters(): Promise<void> {
        isLoading = true;
        try {
            const response = await fetch("/api/pension-planner");
            if (response.ok) {
                const data = await response.json();
                isAuthenticated = data.authenticated;

                if (data.authenticated && data.hasData) {
                    currentValue = data.currentValue;
                    contributionAmount = data.contributionAmount;
                    contributionFrequency = data.contributionFrequency;
                    expectedAnnualReturn = data.expectedAnnualReturn;
                    expectedAnnualInflation = data.expectedAnnualInflation;
                    currentAge = data.currentAge;
                    yearsToProject = data.yearsToProject;
                    taxFreeLumpSumPercentage = data.taxFreeLumpSumPercentage;
                }
            }
        } catch (error) {
            console.error("Failed to load saved parameters:", error);
        } finally {
            isLoading = false;
        }
    }

    async function saveParametersToServer(): Promise<void> {
        if (!isAuthenticated) {
            return;
        }

        try {
            const antiForgeryToken = (document.querySelector('input[name="__RequestVerificationToken"]') as HTMLInputElement)?.value;

            await fetch("/api/pension-planner", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    RequestVerificationToken: antiForgeryToken || "",
                },
                body: JSON.stringify({
                    currentValue,
                    contributionAmount,
                    contributionFrequency,
                    expectedAnnualReturn,
                    expectedAnnualInflation,
                    currentAge,
                    yearsToProject,
                    taxFreeLumpSumPercentage,
                }),
            });

            lastSaveTime = Date.now();
        } catch (error) {
            console.error("Failed to save parameters:", error);
        }
    }

    function handleCalculate(): void {
        try {
            calculationError = null;

            const inputs: PensionPlannerInputs = {
                currentValue,
                contributionAmount,
                contributionFrequency,
                expectedAnnualReturn,
                expectedAnnualInflation,
                currentAge,
                yearsToProject,
                taxFreeLumpSumPercentage,
            };

            result = PensionCalculator.calculate(inputs);
        } catch (error) {
            calculationError = error instanceof Error ? error.message : "An unknown error occurred";
            result = null;
        }
    }

    // Debounced auto-save
    let saveTimeoutId: number | null = null;
    const SAVE_DEBOUNCE_MS = 2000;

    function scheduleAutoSave(): void {
        if (!isAuthenticated || !initialized || !isValid) {
            return;
        }

        if (saveTimeoutId !== null) {
            clearTimeout(saveTimeoutId);
        }

        saveTimeoutId = window.setTimeout(() => {
            saveParametersToServer();
            saveTimeoutId = null;
        }, SAVE_DEBOUNCE_MS);
    }

    // Auto-calculate on mount and when inputs change
    let initialized = $state(false);

    $effect(() => {
        if (!initialized) {
            loadParameters().then(() => {
                initialized = true;
                handleCalculate();
            });
        } else {
            handleCalculate();
            scheduleAutoSave();
        }
    });

    // Validation functions
    function validateCurrentValue(value: number): string {
        if (value < 0) return "Current value cannot be negative";
        if (value > 10000000) return "Warning: Pension value seems unusually high";
        return "";
    }

    function validateContributionAmount(value: number): string {
        if (value < 0) return "Contribution amount cannot be negative";
        const annualContribution = contributionFrequency === "monthly" ? value * 12 : value;
        if (annualContribution > 60000) return "Warning: Annual contribution exceeds typical annual allowance (£60,000)";
        return "";
    }

    function validateExpectedAnnualReturn(value: number): string {
        if (value < -100) return "Expected return cannot be less than -100%";
        if (value > 100) return "Expected return cannot exceed 100%";
        if (value < 0) return "Warning: Negative returns will decrease your pension value";
        if (value > 15) return "Warning: Returns above 15% are historically uncommon";
        return "";
    }

    function validateExpectedAnnualInflation(value: number): string {
        if (value < -100) return "Inflation cannot be less than -100%";
        if (value > 100) return "Inflation cannot exceed 100%";
        if (value < 0) return "Warning: Negative inflation (deflation) is uncommon";
        if (value > 10) return "Warning: Inflation above 10% would significantly erode purchasing power";
        return "";
    }

    function validateCurrentAge(value: number): string {
        if (value < 0) return "Age cannot be negative";
        if (value > 150) return "Age cannot exceed 150";
        if (value < 18) return "Warning: SIPP pensions typically require minimum age of 18";
        if (value > 75) return "Warning: Most pensions must be accessed by age 75";
        return "";
    }

    function validateYearsToProject(value: number): string {
        if (value < 1) return "Years to project must be at least 1";
        if (value > 100) return "Years to project cannot exceed 100";
        const finalAge = currentAge + value;
        if (finalAge > 150) return "Warning: Final age exceeds 150";
        if (finalAge < 55) return "Warning: SIPP pensions typically cannot be accessed before age 55";
        return "";
    }

    function validateTaxFreeLumpSumPercentage(value: number): string {
        if (value < 0) return "Tax-free lump sum cannot be negative";
        if (value > 100) return "Tax-free lump sum cannot exceed 100%";
        if (value > 25) return "Warning: UK SIPP rules typically limit tax-free lump sum to 25%";
        return "";
    }

    // Use $derived to make validation errors reactive
    let validationErrors = $derived({
        currentValue: validateCurrentValue(currentValue),
        contributionAmount: validateContributionAmount(contributionAmount),
        expectedAnnualReturn: validateExpectedAnnualReturn(expectedAnnualReturn),
        expectedAnnualInflation: validateExpectedAnnualInflation(expectedAnnualInflation),
        currentAge: validateCurrentAge(currentAge),
        yearsToProject: validateYearsToProject(yearsToProject),
        taxFreeLumpSumPercentage: validateTaxFreeLumpSumPercentage(taxFreeLumpSumPercentage),
    });

    // Check if all inputs are valid (no non-warning errors)
    let isValid = $derived(!Object.values(validationErrors).some((error) => error !== "" && !error.startsWith("Warning:")));

    function formatCurrency(value: number): string {
        return new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }

    function formatPercent(value: number): string {
        return `${value.toFixed(2)}%`;
    }

    function formatNumber(value: number): string {
        return new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }
</script>

<div class="pension-planner">
    <div class="box">
        <!-- Input Form -->
        <div class="columns is-multiline">
            <!-- Current Pension Value -->
            <div class="column is-half">
                <div class="field">
                    <label class="label" for="current-value">Current Pension Value</label>
                    <div class="control has-icons-left">
                        <input id="current-value" class="input" class:is-loading={isLoading} type="number" bind:value={currentValue} min="0" step="1000" />
                        <span class="icon is-small is-left">
                            <i class="fas fa-pound-sign"></i>
                        </span>
                    </div>
                    {#if validationErrors.currentValue}
                        <p class="help {validationErrors.currentValue.startsWith('Warning:') ? 'has-text-warning' : 'is-danger'}">
                            {validationErrors.currentValue}
                        </p>
                    {/if}
                </div>
            </div>

            <!-- Contribution Amount -->
            <div class="column is-half">
                <div class="field">
                    <label class="label" for="contribution-amount">Contribution Amount</label>
                    <div class="control has-icons-left">
                        <input id="contribution-amount" class="input" class:is-loading={isLoading} type="number" bind:value={contributionAmount} min="0" step="100" />
                        <span class="icon is-small is-left">
                            <i class="fas fa-pound-sign"></i>
                        </span>
                    </div>
                    {#if validationErrors.contributionAmount}
                        <p class="help {validationErrors.contributionAmount.startsWith('Warning:') ? 'has-text-warning' : 'is-danger'}">
                            {validationErrors.contributionAmount}
                        </p>
                    {/if}
                </div>
            </div>

            <!-- Contribution Frequency -->
            <div class="column is-half">
                <div class="field">
                    <label class="label" for="contribution-frequency">Contribution Frequency</label>
                    <div class="control">
                        <div class="select is-fullwidth">
                            <select id="contribution-frequency" class:is-loading={isLoading} bind:value={contributionFrequency} disabled={isLoading}>
                                <option value="monthly">Monthly</option>
                                <option value="annual">Annual</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Expected Annual Return -->
            <div class="column is-half">
                <div class="field">
                    <label class="label" for="expected-return">Expected Annual Return (%)</label>
                    <div class="control has-icons-left">
                        <input id="expected-return" class="input" class:is-loading={isLoading} type="number" bind:value={expectedAnnualReturn} min="-100" max="100" step="0.5" />
                        <span class="icon is-small is-left">
                            <i class="fas fa-chart-line"></i>
                        </span>
                    </div>
                    {#if validationErrors.expectedAnnualReturn}
                        <p class="help {validationErrors.expectedAnnualReturn.startsWith('Warning:') ? 'has-text-warning' : 'is-danger'}">
                            {validationErrors.expectedAnnualReturn}
                        </p>
                    {/if}
                </div>
            </div>

            <!-- Expected Annual Inflation -->
            <div class="column is-half">
                <div class="field">
                    <label class="label" for="expected-inflation">Expected Annual Inflation (%)</label>
                    <div class="control has-icons-left">
                        <input id="expected-inflation" class="input" class:is-loading={isLoading} type="number" bind:value={expectedAnnualInflation} min="-100" max="100" step="0.5" />
                        <span class="icon is-small is-left">
                            <i class="fas fa-chart-line"></i>
                        </span>
                    </div>
                    {#if validationErrors.expectedAnnualInflation}
                        <p class="help {validationErrors.expectedAnnualInflation.startsWith('Warning:') ? 'has-text-warning' : 'is-danger'}">
                            {validationErrors.expectedAnnualInflation}
                        </p>
                    {/if}
                </div>
            </div>

            <!-- Current Age -->
            <div class="column is-half">
                <div class="field">
                    <label class="label" for="current-age">Current Age</label>
                    <div class="control has-icons-left">
                        <input id="current-age" class="input" class:is-loading={isLoading} type="number" bind:value={currentAge} min="0" max="120" />
                        <span class="icon is-small is-left">
                            <i class="fas fa-user"></i>
                        </span>
                    </div>
                    {#if validationErrors.currentAge}
                        <p class="help {validationErrors.currentAge.startsWith('Warning:') ? 'has-text-warning' : 'is-danger'}">
                            {validationErrors.currentAge}
                        </p>
                    {/if}
                </div>
            </div>

            <!-- Years to Project -->
            <div class="column is-half">
                <div class="field">
                    <label class="label" for="years-to-project">Years to Project</label>
                    <div class="control has-icons-left">
                        <input id="years-to-project" class="input" class:is-loading={isLoading} type="number" bind:value={yearsToProject} min="0" max="100" />
                        <span class="icon is-small is-left">
                            <i class="fas fa-calendar"></i>
                        </span>
                    </div>
                    {#if validationErrors.yearsToProject}
                        <p class="help {validationErrors.yearsToProject.startsWith('Warning:') ? 'has-text-warning' : 'is-danger'}">
                            {validationErrors.yearsToProject}
                        </p>
                    {/if}
                </div>
            </div>

            <!-- Tax-Free Lump Sum Percentage -->
            <div class="column is-half">
                <div class="field">
                    <label class="label" for="tax-free-lump-sum">Tax-Free Lump Sum (%)</label>
                    <div class="control has-icons-left">
                        <input id="tax-free-lump-sum" class="input" class:is-loading={isLoading} type="number" bind:value={taxFreeLumpSumPercentage} min="0" max="100" step="1" />
                        <span class="icon is-small is-left">
                            <i class="fas fa-percent"></i>
                        </span>
                    </div>
                    {#if validationErrors.taxFreeLumpSumPercentage}
                        <p class="help {validationErrors.taxFreeLumpSumPercentage.startsWith('Warning:') ? 'has-text-warning' : 'is-danger'}">
                            {validationErrors.taxFreeLumpSumPercentage}
                        </p>
                    {/if}
                    <p class="help">Maximum 25% allowed tax-free under UK SIPP rules</p>
                </div>
            </div>
        </div>

        <!-- Error Message -->
        {#if calculationError}
            <div class="notification is-danger mt-4">
                <p><strong>Error:</strong> {calculationError}</p>
            </div>
        {/if}

        <!-- Results -->
        {#if result && !calculationError}
            <div class="box mt-5" style="background-color: #f5f5f5;">
                <h3 class="title is-5 mb-3">Projection Results</h3>

                <p class="help mb-3">* (Real Terms) - Adjusted for inflation to today's purchasing power</p>

                <!-- Summary Results -->
                <div class="columns is-multiline">
                    <div class="column is-half">
                        <div class="notification is-light">
                            <p class="heading mb-2">Age at Projection End</p>
                            <p class="title is-4">{result.finalAge} years</p>
                        </div>
                    </div>

                    <div class="column is-half">
                        <div class="notification is-light">
                            <p class="heading mb-2">Total Contributions</p>
                            <p class="title is-4">{formatCurrency(result.totalContributions)}</p>
                        </div>
                    </div>

                    <div class="column is-half">
                        <div class="notification is-light">
                            <p class="heading mb-2">Total Pension Value (Nominal)</p>
                            <p class="title is-4">{formatCurrency(result.finalNominalValue)}</p>
                        </div>
                    </div>

                    <div class="column is-half">
                        <div class="notification is-light">
                            <p class="heading mb-2">Total Pension Value (Real Terms)</p>
                            <p class="title is-4">{formatCurrency(result.finalRealTermsValue)}</p>
                        </div>
                    </div>

                    <div class="column is-half">
                        <div class="notification is-light">
                            <p class="heading mb-2">Total Investment Growth</p>
                            <p class="title is-4">{formatCurrency(result.totalInvestmentGrowth)}</p>
                        </div>
                    </div>
                </div>

                <!-- Tax-Free Lump Sum -->
                <h4 class="title is-6 mt-4">Tax-Free Lump Sum at Retirement ({taxFreeLumpSumPercentage}%)</h4>
                <div class="columns">
                    <div class="column is-half">
                        <div class="notification is-light">
                            <p class="heading mb-2">Lump Sum (Nominal)</p>
                            <p class="title is-5">{formatCurrency(result.taxFreeLumpSumNominal)}</p>
                        </div>
                    </div>
                    <div class="column is-half">
                        <div class="notification is-light">
                            <p class="heading mb-2">Lump Sum (Real Terms)</p>
                            <p class="title is-5">{formatCurrency(result.taxFreeLumpSumReal)}</p>
                        </div>
                    </div>
                </div>

                <!-- Remaining Pot -->
                <h4 class="title is-6 mt-4">Remaining Pension Pot (After Lump Sum)</h4>
                <div class="columns">
                    <div class="column is-half">
                        <div class="notification is-light">
                            <p class="heading mb-2">Remaining Pot (Nominal)</p>
                            <p class="title is-5">{formatCurrency(result.remainingPotNominal)}</p>
                        </div>
                    </div>
                    <div class="column is-half">
                        <div class="notification is-light">
                            <p class="heading mb-2">Remaining Pot (Real Terms)</p>
                            <p class="title is-5">{formatCurrency(result.remainingPotReal)}</p>
                        </div>
                    </div>
                </div>

                <!-- Year-by-Year Breakdown -->
                <h4 class="title is-6 mt-4">Year-by-Year Projection</h4>
                <div class="table-container">
                    <table class="table is-striped is-hoverable is-fullwidth">
                        <thead>
                            <tr>
                                <th>Year</th>
                                <th>Age</th>
                                <th>Nominal Value</th>
                                <th>Real Terms Value</th>
                                <th>Total Contributions</th>
                                <th>Investment Growth</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each result.projections as projection}
                                <tr>
                                    <td>{projection.year}</td>
                                    <td>{projection.age}</td>
                                    <td>{formatCurrency(projection.nominalValue)}</td>
                                    <td>{formatCurrency(projection.realTermsValue)}</td>
                                    <td>{formatCurrency(projection.totalContributions)}</td>
                                    <td>{formatCurrency(projection.investmentGrowth)}</td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            </div>
        {/if}
    </div>
</div>

<style>
    .pension-planner {
        font-family: InterVariable, sans-serif;
    }

    .pension-planner .table-container {
        max-height: 400px;
        overflow-y: auto;
    }

    .pension-planner .notification {
        margin-bottom: 0;
    }

    .pension-planner .is-loading {
        opacity: 0.6;
        pointer-events: none;
        cursor: not-allowed;
    }
</style>
